import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  first_name: "", last_name: "", birth_date: "", sex: "",
  guardian_last_name: "", guardian_first_name: "", guardian_email: "",
  guardian2_last_name: "", guardian2_first_name: "", guardian2_email: "",
};

const hasG2 = (f) => !!(f.guardian2_last_name || f.guardian2_first_name || f.guardian2_email);

// Bloc de saisie d'un responsable légal (1 ou 2).
function GuardianBlock({ num, prefix, form, set, onRemove }) {
  const testPrefix = num === 1 ? "guardian" : "guardian2";
  return (
    <div className="mt-1 border-t border-slate-100 pt-3 dark:border-white/10" data-testid={`${testPrefix}-block`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Responsable légal {num}{num === 2 && <span className="ml-1 font-normal normal-case">(facultatif)</span>}
        </p>
        {onRemove && (
          <button type="button" onClick={onRemove} className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700" data-testid={`remove-${testPrefix}`}>
            <Trash2 size={13} /> Supprimer
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${prefix}last`}>Nom</Label>
          <Input id={`${prefix}last`} value={form[`${prefix}last_name`]} onChange={set(`${prefix}last_name`)} data-testid={`${testPrefix}-last-name`} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${prefix}first`}>Prénom</Label>
          <Input id={`${prefix}first`} value={form[`${prefix}first_name`]} onChange={set(`${prefix}first_name`)} data-testid={`${testPrefix}-first-name`} />
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <Label htmlFor={`${prefix}email`}>Adresse e-mail</Label>
        <Input id={`${prefix}email`} type="email" value={form[`${prefix}email`]} onChange={set(`${prefix}email`)} data-testid={`${testPrefix}-email`} />
      </div>
    </div>
  );
}

// Formulaire d'ajout / d'édition d'un élève (+ jusqu'à 2 responsables légaux).
// mode="add" -> POST ; mode="edit" -> PATCH. onDone(credential?) après succès.
export function StudentFormDialog({ open, onOpenChange, mode, classId, student, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [showG2, setShowG2] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const f = mode === "edit" && student ? {
        first_name: student.first_name || "", last_name: student.last_name || "",
        birth_date: student.birth_date || "", sex: student.sex || "",
        guardian_last_name: student.guardian_last_name || "",
        guardian_first_name: student.guardian_first_name || "",
        guardian_email: student.guardian_email || "",
        guardian2_last_name: student.guardian2_last_name || "",
        guardian2_first_name: student.guardian2_first_name || "",
        guardian2_email: student.guardian2_email || "",
      } : EMPTY;
      setForm(f);
      setShowG2(hasG2(f));
    }
  }, [open, mode, student]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const removeG1 = () => setForm((f) => ({ ...f, guardian_last_name: "", guardian_first_name: "", guardian_email: "" }));
  const removeG2 = () => {
    setForm((f) => ({ ...f, guardian2_last_name: "", guardian2_first_name: "", guardian2_email: "" }));
    setShowG2(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Le nom et le prénom sont obligatoires.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "edit") {
        const { data } = await api.patch(`/students/${student.id}`, form);
        toast.success("Élève modifié.");
        onDone?.(data.new_credentials?.length ? { credentials: data.new_credentials } : undefined);
      } else {
        const { data } = await api.post(`/classes/${classId}/students`, form);
        toast.success(`${data.student_name} ajouté(e).`);
        onDone?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = mode === "edit" ? "Enregistrer" : "Ajouter l'élève";
  const g1Filled = !!(form.guardian_last_name || form.guardian_first_name || form.guardian_email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Modifier l'élève" : "Ajouter un élève"}</DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Mettez à jour les informations de l'élève et de ses responsables légaux."
              : "Un passeport et un compte parent par responsable seront créés automatiquement."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="last_name">Nom *</Label>
              <Input id="last_name" value={form.last_name} onChange={set("last_name")} autoFocus data-testid="student-last-name" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="first_name">Prénom *</Label>
              <Input id="first_name" value={form.first_name} onChange={set("first_name")} data-testid="student-first-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="birth_date">Date de naissance</Label>
              <Input id="birth_date" type="date" value={form.birth_date} onChange={set("birth_date")} data-testid="student-birth-date" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sex">Sexe</Label>
              <select
                id="sex" value={form.sex} onChange={set("sex")}
                className="h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="student-sex"
              >
                <option value="">—</option>
                <option value="F">Fille</option>
                <option value="M">Garçon</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          <GuardianBlock num={1} prefix="guardian_" form={form} set={set} onRemove={mode === "edit" && g1Filled ? removeG1 : null} />

          {showG2 ? (
            <GuardianBlock num={2} prefix="guardian2_" form={form} set={set} onRemove={removeG2} />
          ) : (
            <button
              type="button"
              onClick={() => setShowG2(true)}
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-blue-700 hover:text-blue-900 dark:text-blue-400"
              data-testid="add-guardian2"
            >
              <UserPlus size={15} /> Ajouter un deuxième responsable légal
            </button>
          )}

          <DialogFooter className="pt-2">
            <Button type="submit" disabled={loading} className="rounded-xl" data-testid="submit-student-button">
              {loading ? <Loader2 size={18} className="animate-spin" /> : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
